// Settings › Returns › Royal Mail labels: the returns label pool (Portal-BE
// labelPool.js, migration 261). Pre-generated Royal Mail Tracked 48 return
// labels, uploaded in bulk (several PDFs at once, drag and drop), counted by
// status, searchable by tracking number or return, each openable as its PDF,
// and voidable (available labels only, with a reason). The pool only hands
// out real labels on production; everywhere else issues specimens.
import { DragEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { Upload, FileText, Ban, AlertTriangle, RefreshCw } from "lucide-react";
import { useTranslation } from "../../i18n";
import {
    LabelPoolSummary, PoolLabel, PoolUploadResult, getLabelPoolSummary, listPoolLabels, uploadPoolLabels, voidPoolLabel,
    poolLabelPdfPath, openProtectedFile,
} from "../../api/returns";

const STATUSES = ["available", "issued", "downloaded", "received", "void"] as const;
const PILL: Record<string, string> = { available: "green", issued: "amber", downloaded: "blue", received: "teal", void: "grey" };

/** The low-stock line, shared with the Returns page. */
export const LowStockWarning = ({ summary }: { summary: LabelPoolSummary | null }) => {
    const { t } = useTranslation();
    if (!summary || !summary.low_stock || summary.unavailable) return null;
    return (
        <p className="rt-warn" role="status" data-testid="rm-low-stock">
            <AlertTriangle size={14} /> {t("rm_low_stock", { n: String(summary.available_usable), min: String(summary.low_stock_threshold) })}
        </p>
    );
};

const RoyalMailLabelPool = ({ canEdit }: { canEdit: boolean }) => {
    const { t, language } = useTranslation();
    const when = new Intl.DateTimeFormat(language === "pl" ? "pl-PL" : "en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
    const fmt = (iso: string | null) => (iso ? when.format(new Date(iso)) : "-");
    const [summary, setSummary] = useState<LabelPoolSummary | null>(null);
    const [results, setResults] = useState<PoolUploadResult[]>([]);
    const [busy, setBusy] = useState(false);
    const [drag, setDrag] = useState(false);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [labels, setLabels] = useState<PoolLabel[] | null>(null);
    const [voiding, setVoiding] = useState<number | null>(null);
    const [voidReason, setVoidReason] = useState("");
    const input = useRef<HTMLInputElement>(null);

    const loadSummary = useCallback(async () => {
        const r = await getLabelPoolSummary();
        setSummary(r.ok && r.data ? r.data : null);
    }, []);
    const loadLabels = useCallback(async () => {
        const r = await listPoolLabels({ q: q.trim() || undefined, status: status || undefined });
        setLabels(r.ok && r.data ? r.data.labels : []);
    }, [q, status]);
    useEffect(() => { void loadSummary(); }, [loadSummary]);
    useEffect(() => {
        const timer = setTimeout(() => { void loadLabels(); }, 250);
        return () => clearTimeout(timer);
    }, [loadLabels]);

    const send = async (files: File[]) => {
        const pdfs = files.filter((f) => /\.pdf$/i.test(f.name) || f.type === "application/pdf");
        if (!pdfs.length) { toast.error(t("rm_pdf_only")); return; }
        setBusy(true);
        try {
            const r = await uploadPoolLabels(pdfs);
            if (r.ok && r.data) {
                setResults(r.data.files);
                if (r.data.summary) setSummary(r.data.summary);
                void loadLabels();
            } else toast.error(r.message || t("rm_upload_failed"));
        } finally {
            setBusy(false);
        }
    };
    const onDrop = (e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDrag(false);
        if (!canEdit || busy) return;
        void send(Array.from(e.dataTransfer.files ?? []));
    };
    const doVoid = async (id: number) => {
        const r = await voidPoolLabel(id, voidReason.trim());
        if (r.ok) {
            toast.success(t("rm_voided"));
            setVoiding(null);
            setVoidReason("");
            void loadSummary();
            void loadLabels();
        } else toast.error(r.message || t("rs_save_failed"));
    };
    const openPdf = async (id: number) => {
        const r = await openProtectedFile(poolLabelPdfPath(id));
        if (!r.ok) toast.error(t("rt_file_failed"));
    };

    if (summary?.unavailable) return <p className="rt-muted rt-small">{t("rm_not_ready")}</p>;

    return (
        <div className="rm-pool" data-testid="rm-pool">
            <LowStockWarning summary={summary} />
            {summary && !summary.live && <p className="rt-muted rt-small">{t("rm_specimen_note")}</p>}
            <div className="rm-counts">
                {STATUSES.map((s) => (
                    <div key={s} className="rm-count" data-testid={`rm-count-${s}`}>
                        <strong>{summary ? summary.counts[s] ?? 0 : "-"}</strong>
                        <span>{t(`rm_status_${s}`)}</span>
                    </div>
                ))}
                <button type="button" className="rt-icon-btn" aria-label={t("rt_refresh")} onClick={() => { void loadSummary(); void loadLabels(); }}><RefreshCw size={14} /></button>
            </div>

            {canEdit && (
                <div className={`rm-drop${drag ? " rm-drop--over" : ""}`} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop}>
                    <Upload size={18} />
                    <span>{busy ? t("rm_uploading") : t("rm_drop_here")}</span>
                    <button type="button" className="btn btn-sm rt-btn btn-secondary" disabled={busy} onClick={() => input.current?.click()}>{t("rm_choose_files")}</button>
                    <input ref={input} type="file" accept="application/pdf,.pdf" multiple hidden data-testid="rm-file-input"
                        onChange={(e) => { const files = Array.from(e.target.files ?? []); e.target.value = ""; if (files.length) void send(files); }} />
                </div>
            )}
            {results.length > 0 && (
                <ul className="rm-results" aria-live="polite">
                    {results.map((r, i) => (
                        <li key={`${r.filename}-${i}`} className={r.error ? "rm-result rm-result--bad" : "rm-result"}>
                            <strong>{r.filename}</strong>
                            {r.error
                                ? <span> {r.error}</span>
                                : <span> {t("rm_result", { pages: String(r.page_count), added: String(r.new), dup: String(r.duplicate), bad: String(r.rejected) })}
                                    {r.first_tracking ? ` · ${r.first_tracking} – ${r.last_tracking}` : ""}</span>}
                            {(r.warnings ?? []).map((w) => <span key={w} className="rt-block rt-small rm-warn-text">{w}</span>)}
                            {(r.rejected_pages ?? []).slice(0, 5).map((p) => <span key={p.page} className="rt-block rt-small rt-muted">{t("rm_page", { n: String(p.page) })}: {p.reason}</span>)}
                            {(r.rejected_pages?.length ?? 0) > 5 && <span className="rt-block rt-small rt-muted">{t("rm_more_rejected", { n: String((r.rejected_pages?.length ?? 0) - 5) })}</span>}
                        </li>
                    ))}
                </ul>
            )}

            <div className="rm-search">
                <input className="rt-input" placeholder={t("rm_search")} aria-label={t("rm_search")} value={q} onChange={(e) => setQ(e.target.value)} />
                <select className="rt-input" aria-label={t("rm_status_filter")} value={status} onChange={(e) => setStatus(e.target.value)}>
                    <option value="">{t("rm_all_statuses")}</option>
                    {STATUSES.map((s) => <option key={s} value={s}>{t(`rm_status_${s}`)}</option>)}
                </select>
            </div>
            <div className="rs-table-wrap">
                <table className="rs-table rm-table">
                    <thead>
                        <tr><th>{t("rm_tracking")}</th><th>{t("rm_status")}</th><th>{t("rm_return")}</th><th>{t("rm_issued")}</th><th>{t("rm_downloaded")}</th><th>{t("rm_received")}</th><th aria-label={t("rs_actions")} /></tr>
                    </thead>
                    <tbody>
                        {labels === null && <tr><td colSpan={7} className="rt-muted">{t("loading")}</td></tr>}
                        {labels?.length === 0 && <tr><td colSpan={7} className="rt-muted">{t("rm_none")}</td></tr>}
                        {labels?.map((l) => (
                            <tr key={l.id}>
                                <td><span className="rt-mono">{l.tracking_number}</span><span className="rt-block rt-small rt-muted rt-mono">{l.mailmark_ref}</span></td>
                                <td><span className={`rt-pill rt-pill--${PILL[l.status] ?? "grey"}`}>{t(`rm_status_${l.status}`)}</span>
                                    {l.status === "void" && l.void_reason && <span className="rt-block rt-small rt-muted">{l.void_reason}</span>}</td>
                                <td>{l.return_reference
                                    ? <Link className="rt-ref-link rt-mono" to={`/admin/returns?tab=all&ref=${encodeURIComponent(l.return_reference)}`}>{l.return_reference}</Link>
                                    : "-"}</td>
                                <td className="rt-small">{fmt(l.issued_at)}</td>
                                <td className="rt-small">{l.first_downloaded_at ? `${fmt(l.first_downloaded_at)}${l.download_count > 1 ? ` (×${l.download_count})` : ""}` : "-"}</td>
                                <td className="rt-small">{l.received_at ? `${fmt(l.received_at)}${l.received_by_name ? ` · ${l.received_by_name}` : ""}` : "-"}</td>
                                <td className="rm-actions">
                                    <button type="button" className="rt-icon-btn" aria-label={t("rm_open_pdf", { tracking: l.tracking_number })} onClick={() => openPdf(l.id)}><FileText size={14} /></button>
                                    {canEdit && l.status === "available" && (voiding === l.id
                                        ? (
                                            <span className="rs-inline">
                                                <input className="rt-input" autoFocus placeholder={t("rm_void_reason")} aria-label={t("rm_void_reason")} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
                                                <button type="button" className="btn btn-sm rt-btn btn-secondary" disabled={!voidReason.trim()} onClick={() => doVoid(l.id)}>{t("rm_void")}</button>
                                                <button type="button" className="btn btn-sm rt-btn btn-ghost" onClick={() => { setVoiding(null); setVoidReason(""); }}>{t("cancel")}</button>
                                            </span>
                                        )
                                        : <button type="button" className="rt-icon-btn" aria-label={t("rm_void_label", { tracking: l.tracking_number })} onClick={() => { setVoiding(l.id); setVoidReason(""); }}><Ban size={14} /></button>)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default RoyalMailLabelPool;
